import React from 'react';
import { Info, Calculator, FileText, BarChart2, Clock, AlertTriangle, BookOpen, Building2 } from 'lucide-react';

/**
 * Компонент с информацией о налоге на прибыль и рекомендациями
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-indigo-500" />
        Всё о налоге на прибыль организаций: основные положения и особенности
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Что такое налог на прибыль?
        </h3>
        <p>
          <strong>Налог на прибыль организаций</strong> — это прямой налог, взимаемый с прибыли организации, полученной за отчетный (налоговый) период. Порядок исчисления и уплаты налога на прибыль организаций установлен главой 25 Налогового кодекса Российской Федерации.
        </p>
        <p>
          Прибылью в соответствии со ст. 247 НК РФ признаются полученные доходы, уменьшенные на величину произведенных расходов, которые определяются в соответствии с главой 25 НК РФ.
        </p>

        <h3 className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-indigo-500" />
          Налогоплательщики и объект налогообложения
        </h3>
        <p>
          Налогоплательщиками налога на прибыль организаций являются:
        </p>
        <ul>
          <li>Российские организации</li>
          <li>Иностранные организации, осуществляющие деятельность в РФ через постоянные представительства</li>
          <li>Иностранные организации, получающие доходы от источников в РФ</li>
        </ul>
        <p>
          Не являются налогоплательщиками налога на прибыль организации, применяющие специальные налоговые режимы (УСН, ЕСХН, НПД) и государственные учреждения. Объектом налогообложения по налогу на прибыль организаций признается прибыль, полученная налогоплательщиком.
        </p>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-green-500" />
          Налоговая база и налоговые ставки
        </h3>
        <p>
          <strong>Налоговая база</strong> — это денежное выражение прибыли, подлежащей налогообложению. Налоговая база определяется как разница между полученными доходами и произведенными расходами.
        </p>
        <p>
          Доходы организации делятся на два основных вида:
        </p>
        <ul>
          <li><strong>Доходы от реализации</strong> — выручка от реализации товаров, работ, услуг и имущественных прав (ст. 249 НК РФ)</li>
          <li><strong>Внереализационные доходы</strong> — доходы, не связанные с реализацией, например, проценты по депозитам, доходы от сдачи имущества в аренду, штрафы, пени, неустойки и т.д. (ст. 250 НК РФ)</li>
        </ul>
        <p>
          Расходы организации также делятся на два вида:
        </p>
        <ul>
          <li><strong>Расходы, связанные с производством и реализацией</strong> — материальные расходы, расходы на оплату труда, амортизация и другие расходы (ст. 253-264 НК РФ)</li>
          <li><strong>Внереализационные расходы</strong> — расходы, не связанные с производством и реализацией, например, проценты по кредитам, расходы на содержание имущества, переданного в аренду, и т.д. (ст. 265 НК РФ)</li>
        </ul>
        <p>
          <strong>Основная ставка налога на прибыль организаций</strong> составляет 20%, из них:
        </p>
        <ul>
          <li>3% зачисляется в федеральный бюджет</li>
          <li>17% зачисляется в бюджет субъекта РФ</li>
        </ul>
        <p>
          Для отдельных категорий налогоплательщиков предусмотрены пониженные ставки налога. Например:
        </p>
        <ul>
          <li>IT-компании: 3% в федеральный бюджет и 0% в региональный (до 2024 года)</li>
          <li>Резиденты особых экономических зон (ОЭЗ): ставка в региональный бюджет может быть снижена до 0%</li>
          <li>Ставка 0% по доходам в виде дивидендов при соблюдении определенных условий (доля участия более 50%, срок владения более 365 дней)</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Изменения налоговых ставок в 2023-2024 годах</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            По состоянию на 2023 год сохраняется распределение ставки 20% между бюджетами как 3% в федеральный бюджет и 17% в региональный. Для IT-компаний действует льготная ставка 0% до конца 2024 года. Следите за изменениями в налоговом законодательстве, так как ставки и льготы могут меняться.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-500" />
          Налоговый и отчетный периоды
        </h3>
        <p>
          <strong>Налоговым периодом</strong> по налогу на прибыль организаций признается календарный год.
        </p>
        <p>
          <strong>Отчетными периодами</strong> признаются:
        </p>
        <ul>
          <li>I квартал</li>
          <li>Полугодие</li>
          <li>Девять месяцев</li>
        </ul>
        <p>
          По итогам каждого отчетного периода налогоплательщики исчисляют сумму авансового платежа исходя из ставки налога и прибыли, рассчитанной нарастающим итогом с начала налогового периода до окончания отчетного периода.
        </p>
        <p>
          Для организаций, у которых за предыдущие четыре квартала доходы от реализации превышали в среднем 15 млн рублей за каждый квартал, установлена обязанность по уплате ежемесячных авансовых платежей.
        </p>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" />
          Особенности исчисления и уплаты налога
        </h3>
        <p>
          Порядок расчета налога на прибыль состоит из следующих этапов:
        </p>
        <ol>
          <li>Определение доходов, учитываемых при налогообложении</li>
          <li>Определение расходов, учитываемых при налогообложении</li>
          <li>Расчет налоговой базы как разницы между доходами и расходами</li>
          <li>Применение налоговых ставок к налоговой базе</li>
          <li>Расчет суммы налога, подлежащей уплате в бюджет</li>
        </ol>
        <p>
          Уплата авансовых платежей, а также сумм налога, подлежащих зачислению в федеральный бюджет и бюджеты субъектов РФ, производится по месту нахождения организации.
        </p>
        <p>
          <strong>Налоговая декларация</strong> по итогам налогового периода представляется не позднее 28 марта года, следующего за истекшим налоговым периодом. Авансовые платежи по итогам отчетного периода уплачиваются не позднее 28 календарных дней со дня окончания отчетного периода.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при расчете налога на прибыль
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Не все расходы учитываются для целей налогообложения (перечень в ст. 270 НК РФ)</li>
            <li>Некоторые расходы могут быть учтены с ограничениями (нормируемые расходы)</li>
            <li>Следите за изменениями в налоговом законодательстве и за судебной практикой</li>
            <li>Соблюдайте требования к документальному оформлению расходов</li>
            <li>Учитывайте правила признания доходов и расходов (метод начисления или кассовый метод)</li>
            <li>При применении специальных льгот (для IT-компаний, резидентов ОЭЗ и др.) необходимо соблюдать условия для их применения</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-indigo-500" />
          Специальные налоговые вычеты и льготы
        </h3>
        <p>
          Налоговый кодекс предусматривает ряд специальных вычетов и льгот при расчете налога на прибыль:
        </p>
        <ul>
          <li><strong>Инвестиционный налоговый вычет</strong> (ст. 286.1 НК РФ) — право уменьшить исчисленную сумму налога на прибыль на расходы, связанные с приобретением или модернизацией основных средств</li>
          <li><strong>Расходы на НИОКР</strong> (ст. 262 НК РФ) — могут учитываться с коэффициентом 1.5, что фактически уменьшает налоговую базу</li>
          <li><strong>Перенос убытков</strong> (ст. 283 НК РФ) — возможность уменьшить налоговую базу текущего периода на сумму убытков прошлых лет (не более чем на 50% налоговой базы)</li>
          <li><strong>Льготы для резидентов особых экономических зон</strong> — пониженные ставки налога в региональный бюджет</li>
          <li><strong>Льготы для IT-компаний</strong> — пониженная ставка налога (0% в региональный бюджет до 2024 года)</li>
        </ul>

        <h3>Типичные ошибки при расчете налога на прибыль</h3>
        <ol>
          <li><strong>Неправильное признание доходов и расходов</strong> — несоблюдение принципов и методов учета (метод начисления/кассовый метод)</li>
          <li><strong>Учет расходов, не уменьшающих налоговую базу</strong> — включение расходов, перечисленных в ст. 270 НК РФ</li>
          <li><strong>Нарушение требований к документальному оформлению</strong> — отсутствие подтверждающих первичных документов</li>
          <li><strong>Неверное применение норм по отдельным видам расходов</strong> — превышение предельных размеров нормируемых расходов</li>
          <li><strong>Ошибки при расчете амортизации</strong> — неверное определение срока полезного использования или метода начисления амортизации</li>
          <li><strong>Некорректный учет убытков прошлых лет</strong> — превышение ограничения в 50% налоговой базы</li>
        </ol>

        <p className="font-semibold mt-6">
          Наш калькулятор поможет вам оценить предварительную сумму налога на прибыль и спланировать налоговую нагрузку. Для подготовки официальной отчетности рекомендуем проконсультироваться с квалифицированным бухгалтером или налоговым специалистом.
        </p>
      </div>
    </article>
  );
};

export default InfoContent;